import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence, selectCompTypes } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateAtticAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { attic, craftsmanQuality: quality, propertyType: subjPropertyType } = subjectImprovements;
  const {
    attic: compAttic,
    craftsmanQuality: compDwellingQuality,
    propertyType: compPropertyType,
  } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (
    isUndefined(zipCode) ||
    isUndefined(compZipCode) ||
    isUndefined(subjPropertyType) ||
    isUndefined(compPropertyType) ||
    isUndefined(quality) ||
    isUndefined(compDwellingQuality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1
  ) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  const atticArea = attic?.area && ORDER_FEATURES.attic.hasFeature(subjectImprovements) ? AloftArea.schema.parse(attic?.area) : undefined;
  const compAtticArea = compAttic?.area && ORDER_FEATURES.attic.hasFeature(compImprovements) ? AloftArea.schema.parse(compAttic?.area) : undefined;

  return adjCalculator.getAtticAdj({
    subject: {
      propertyType: subjPropertyType,
      hasFeature: ORDER_FEATURES.attic.hasFeature(subjectImprovements),
      sqft: atticArea,
      quality: attic?.quality ?? quality,
      zipCode,
    },
    comp: {
      propertyType: compPropertyType,
      hasFeature: ORDER_FEATURES.attic.hasFeature(compImprovements),
      sqft: compAtticArea,
      quality: compAttic?.quality ?? compDwellingQuality,
      zipCode: compZipCode,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const selectAtticAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'attic'),
    (_state, id: string) => id,
    selectCompTypes,
  ],
  calculateAtticAdj,
);

export const selectAtticAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'attic'),
    selectCompSequence,
    selectCompTypes,
  ],
  (adjustmentProps, improvements, propertyIdentification, depreciationRatios, compSequence, compTypes) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateAtticAdj(
        adjustmentProps,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
        compTypes,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);

const selectAppliedAtticAdj = createPriceSelector(
  [selectAtticAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'attic', adjSettings);
  },
);

export const selectAppliedAtticAdjDisplay = createSelector([selectAppliedAtticAdj], (adj) => {
  if (isUndefined(adj)) {
    return undefined;
  }

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedAtticAdjsDisplay = createSelector(
  [selectAtticAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, settings, manualAdjustmentsEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = adjs[propID];
      const manualAdjustment = manualAdjustmentsEntities[propID]?.attic;
      if (isDefined(manualAdjustment)) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[propID] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (isDefined(adj)) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adjs[propID], 'attic', settings),
          ),
          color: getColorFromAdjustment(adjs[propID]),
        };
      }
      return acc;
    }, {}),
);
